// src/review/context/ReviewContext.jsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { goToStep as goToStepAction } from '../../redux/slices/reviewSlice';

const ReviewContext = createContext(null);

/**
 * ReviewProvider - Manages shared state for review flow
 * Avoids prop drilling by providing context to all child components
 */
export const ReviewProvider = ({ modelId, assessmentUuid, children }) => {
  const dispatch = useDispatch();
  const [currentRiskId, setCurrentRiskId] = useState(null);
  const [currentThreatId, setCurrentThreatId] = useState(null);
  const [deepDiveDrawerOpen, setDeepDiveDrawerOpen] = useState(false);

  // Memoized callbacks to avoid re-renders
  const openDeepDive = useCallback(() => {
    setDeepDiveDrawerOpen(true);
  }, []);

  const closeDeepDive = useCallback(() => {
    setDeepDiveDrawerOpen(false);
  }, []);

  const goToStep = useCallback((step) => {
    // Dispatch RTK action to update currentStep in Redux state
    dispatch(goToStepAction({ assessmentUuid, step }));
  }, [dispatch, assessmentUuid]);

  const selectRisk = useCallback((riskId) => {
    setCurrentRiskId(riskId);
  }, []);

  const selectThreat = useCallback((threatId) => {
    setCurrentThreatId(threatId);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    modelId,
    assessmentUuid,
    currentRiskId,
    currentThreatId,
    deepDiveDrawerOpen,
    goToStep,
    selectRisk,
    selectThreat,
    openDeepDive,
    closeDeepDive
  }), [
    modelId,
    assessmentUuid,
    currentRiskId,
    currentThreatId,
    deepDiveDrawerOpen,
    goToStep,
    selectRisk,
    selectThreat,
    openDeepDive,
    closeDeepDive
  ]);

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};

ReviewProvider.propTypes = {
  modelId: PropTypes.string.isRequired,
  assessmentUuid: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

/**
 * useReview - Hook to access review context
 * @throws {Error} If used outside ReviewProvider
 */
export const useReview = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReview must be used within ReviewProvider');
  }
  return context;
};
